/**
 * ✅ RICH CHAT RENDERER v2.0
 *
 * COMPLETELY REDESIGNED to be VISUALLY DISTINCT from markdown.
 * Each block is a separate visual component with:
 * - Clear boundaries and containers
 * - Distinct card-like layouts
 * - Visual separation between blocks
 * - Component-based structure (NOT just styled HTML)
 *
 * @module RichChatRenderer
 */

import React, { useMemo, useState, memo } from 'react';

import {
  Copy,
  Check,
  Download,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  Info,
  FileText,
  Hash,
  List,
  ListOrdered,
  Table2,
  Code2,
  Quote,
  Minus,
  BookOpen,
  Sparkles,
} from 'lucide-react';

import { MarkdownText } from '@/components/RichChat/MarkdownText';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
// ✅ ENTERPRISE FIX: Import Excel Insights component
import type {
  RichChatBlock,
  SectionHeaderBlock,
  SubheaderBlock,
  ParagraphBlock,
  BulletListBlock,
  NumberedListBlock,
  TableBlock,
  CodeBlock,
  CalloutBlock,
  StatusLineBlock,
  ProgressLineBlock,
  MetricsListBlock,
  DividerBlock,
  CitationBlock,
  ExpandableBlock,
  ButtonGroupBlock,
  ExcelInsightsBlock, // ✅ ENTERPRISE FIX: Excel Insights block type
  ArtifactBriefingBlock,
  ArtifactBriefingFile,
  ArtifactFinding,
  ArtifactCitationAnchor,
  StatusTone,
  CalloutVariant,
} from '../../types/richChatTypes';
import ExcelInsights from '@/components/smart-notebook/ExcelInsights';

// ═══════════════════════════════════════════════════════════════════════════
// MAIN RENDERER - Creates visual separation between blocks
// ═══════════════════════════════════════════════════════════════════════════

export interface RichChatRendererProps {
  blocks: RichChatBlock[];
  showEmojis?: boolean;
  compactMode?: boolean;
  onButtonClick?: (action: string) => void;
  // ✅ ENTERPRISE: Citation navigation needs full object (see useCitationNavigation hook)
  onCitationClick?: (citation: any) => void;
}

export const RichChatRenderer: React.FC<RichChatRendererProps> = memo(
  ({ blocks, showEmojis = true, compactMode = false, onButtonClick, onCitationClick }) => {
    if (!blocks || blocks.length === 0) return null;

    return (
      <div className="rich-chat-blocks space-y-5">
        {blocks.map((block, idx) => (
          <div key={block.id ?? `block-${idx}`} className="rich-chat-block animate-fadeIn">
            <BlockRenderer
              block={block}
              showEmojis={showEmojis}
              compactMode={compactMode}
              onButtonClick={onButtonClick}
              onCitationClick={onCitationClick}
            />
          </div>
        ))}
      </div>
    );
  }
);

RichChatRenderer.displayName = 'RichChatRenderer';

// ═══════════════════════════════════════════════════════════════════════════
// BLOCK ROUTER
// ═══════════════════════════════════════════════════════════════════════════

interface BlockRendererProps {
  block: RichChatBlock;
  showEmojis: boolean;
  compactMode: boolean;
  onButtonClick?: (action: string) => void;
  onCitationClick?: (citation: any) => void;
}

const BlockRenderer: React.FC<BlockRendererProps> = memo(
  ({ block, showEmojis, compactMode, onButtonClick, onCitationClick }) => {
    const emoji = showEmojis && block.emoji ? block.emoji : undefined;

    switch (block.type) {
      case 'section_header':
        return <SectionHeader block={block} emoji={emoji} />;
      case 'subheader':
        return <Subheader block={block} emoji={emoji} />;
      case 'paragraph':
        return <Paragraph block={block} emoji={emoji} />;
      case 'bullet_list':
        return <BulletList block={block} showEmojis={showEmojis} />;
      case 'numbered_list':
        return <NumberedList block={block} />;
      case 'table':
        return <Table block={block} />;
      case 'code':
        return <CodeBox block={block} />;
      case 'callout':
        return <Callout block={block} emoji={emoji} />;
      case 'status_line':
        return <StatusLine block={block} emoji={emoji} />;
      case 'progress_line':
        return <ProgressLine block={block} emoji={emoji} />;
      case 'metrics_list':
        return <MetricsList block={block} showEmojis={showEmojis} />;
      case 'divider':
        return <Divider />;
      case 'citation':
        return <Citation block={block} onCitationClick={onCitationClick} />;
      case 'expandable':
        return (
          <Expandable
            block={block}
            showEmojis={showEmojis}
            compactMode={compactMode}
            onButtonClick={onButtonClick}
            onCitationClick={onCitationClick}
          />
        );
      case 'button_group':
        return <ButtonGroup block={block} onButtonClick={onButtonClick} />;
      case 'excel_insights':
        // ✅ ENTERPRISE FIX: Render Excel Insights block
        return <ExcelInsightsBlockRenderer block={block} />;
      case 'artifact_briefing':
        return (
          <ArtifactBriefing
            block={block}
            compactMode={compactMode}
            onCitationClick={onCitationClick}
          />
        );
      default:
        return null;
    }
  }
);

BlockRenderer.displayName = 'BlockRenderer';

// ✅ ENTERPRISE FIX: Excel Insights Block Renderer
const ExcelInsightsBlockRenderer: React.FC<{ block: ExcelInsightsBlock }> = ({ block }) => {
  // Type-safe access to block data
  const data = block.data as ExcelInsightsBlock['data'];

  return (
    <div className="excel-insights-block">
      <ExcelInsights
        metadata={data.metadata || data.summary}
        table={data.table}
        charts={data.charts}
        insights={data.insights}
        verification={data.verification}
        schema={data.schema}
        analysis={data.analysis}
        intelligence={data.intelligence}
        onQueryRequest={(query: string) => {
          console.log('Excel query requested:', query);
          // Handle query request - could trigger new query
        }}
      />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// ✅ ENTERPRISE: ARTIFACT BRIEFING (Citable Executive Summary)
// ═══════════════════════════════════════════════════════════════════════════

type DensityMode = 'executive' | 'compact' | 'analytical';

const CATEGORY_ICON: Record<string, { icon: string; label: string }> = {
  subject: { icon: '🎯', label: 'Subject' },
  parties: { icon: '👥', label: 'Parties' },
  commercial_terms: { icon: '💰', label: 'Commercial' },
  dates: { icon: '📅', label: 'Dates' },
  obligations: { icon: '📌', label: 'Obligations' },
  risks: { icon: '⚠️', label: 'Risks' },
  workflow: { icon: '🧭', label: 'Workflow' },
  tables: { icon: '📊', label: 'Tables' },
  other: { icon: '•', label: 'Other' },
};

function normalizeDocKind(kind?: string | null) {
  const k = String(kind || '').toLowerCase();
  if (!k) return 'unknown';
  if (k.includes('contract')) return 'contract';
  if (k.includes('arabic')) return 'arabic_letter';
  if (k.includes('excel')) return 'excel';
  if (k.includes('diagram')) return 'diagram';
  if (k.includes('image')) return 'image';
  if (k.includes('document')) return 'document';
  return k;
}

function categoryPriorityForDocKind(docKind: string): string[] {
  switch (docKind) {
    case 'arabic_letter':
      return ['subject', 'commercial_terms', 'dates', 'obligations', 'risks', 'parties', 'other'];
    case 'contract':
      return ['parties', 'commercial_terms', 'dates', 'obligations', 'risks', 'other'];
    case 'excel':
      return ['tables', 'commercial_terms', 'dates', 'risks', 'other'];
    case 'diagram':
      return ['workflow', 'risks', 'other'];
    default:
      return ['subject', 'commercial_terms', 'dates', 'obligations', 'risks', 'parties', 'other'];
  }
}

function isFindingCitable(f: ArtifactFinding) {
  const hasAnchors = Array.isArray(f.citationAnchors) && f.citationAnchors.length > 0;
  const hasQuote =
    Array.isArray(f.quotes) && f.quotes.length > 0 && String(f.quotes[0] || '').trim().length > 0;
  return hasAnchors && hasQuote;
}

function buildCitationObject({
  file,
  anchor,
  quote,
}: {
  file: ArtifactBriefingFile;
  anchor: ArtifactCitationAnchor;
  quote?: string | null;
}) {
  return {
    sourceFile: {
      id: file.fileId,
      name: file.fileName,
      originalName: file.fileName,
      type: file.docKind || 'document',
      mimeType: file.mimeType || undefined,
    },
    location: {
      pageNumber: anchor.page ?? null,
      lineNumber: anchor.lineNumber ?? null,
      sheetName: anchor.sheetName ?? null,
      cellRange: anchor.cellRange ?? null,
    },
    content: {
      exactText: quote || null,
      highlightText: quote || null,
    },
    display: {
      fileName: file.fileName,
      locationString: [
        anchor.page ? `Page ${anchor.page}` : null,
        anchor.sheetName ? `${anchor.sheetName}` : null,
        anchor.cellRange ? `Cells ${anchor.cellRange}` : null,
        anchor.lineNumber ? `Line ${anchor.lineNumber}` : null,
      ]
        .filter(Boolean)
        .join(' • '),
    },
    metadata: {
      ledgerUnitId: anchor.ledgerUnitId || null,
      chunkIndex: anchor.chunkIndex ?? null,
    },
  };
}

const ArtifactBriefing: React.FC<{
  block: ArtifactBriefingBlock;
  compactMode: boolean;
  onCitationClick?: (citation: any) => void;
}> = ({ block, compactMode, onCitationClick }) => {
  const [density, setDensity] = useState<DensityMode>(compactMode ? 'compact' : 'executive');
  const [expandedFiles, setExpandedFiles] = useState<Record<string, boolean>>({});

  const files = Array.isArray(block.data?.files) ? block.data.files : [];
  const title = block.title || 'Executive Briefing';
  const narrative = typeof block.data?.narrative === 'string' ? block.data.narrative : null;

  const toggleFile = (fileId: string) => {
    setExpandedFiles(prev => ({ ...prev, [fileId]: !prev[fileId] }));
  };

  // Density is PRESENTATIONAL ONLY (enterprise immutability):
  // - never deletes/filters artifact content
  // - only controls default visibility (collapsed/expanded, quotes shown, count preview)
  const densityView = useMemo(() => {
    if (density === 'analytical') return { maxFindingsPerFile: 12, showQuotes: true };
    if (density === 'compact') return { maxFindingsPerFile: 4, showQuotes: false };
    return { maxFindingsPerFile: 6, showQuotes: false }; // executive default
  }, [density]);

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/10 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white shadow-sm">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">
            {title}
          </div>
          <div className="text-xs text-slate-600 dark:text-slate-400">
            Audit-first • Citable findings only • {files.length} file{files.length === 1 ? '' : 's'}
          </div>
        </div>

        {/* Density toggle */}
        <div className="flex items-center gap-1 rounded-lg bg-white/70 dark:bg-slate-900/60 border border-amber-200/60 dark:border-amber-800/40 p-1">
          {(
            [
              { key: 'executive', label: 'Executive' },
              { key: 'compact', label: 'Compact' },
              { key: 'analytical', label: 'Analytical' },
            ] as const
          ).map(opt => (
            <button
              key={opt.key}
              onClick={() => setDensity(opt.key)}
              className={`px-2 py-1 text-[11px] rounded-md transition-colors ${
                density === opt.key
                  ? 'bg-amber-500 text-white'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-amber-100/70 dark:hover:bg-amber-900/30'
              }`}
              title={`Switch to ${opt.label} view`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Files */}
      <div className="p-4 space-y-3">
        {narrative && (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 p-4">
            <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
              Executive narrative
            </div>
            <div className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap leading-relaxed">
              {narrative}
            </div>
          </div>
        )}
        {files.length === 0 ? (
          <div className="text-sm text-slate-600 dark:text-slate-300">
            No artifact files available.
          </div>
        ) : (
          files.map(file => {
            const fileId = file.fileId || file.fileName;
            const docKind = normalizeDocKind(file.docKind);
            const findingsAll = Array.isArray(file.findings) ? file.findings : [];
            const briefText = typeof file.briefText === 'string' ? file.briefText : null;
            const isExpanded = expandedFiles[fileId] === true || density === 'analytical';

            // Order findings: doc-kind priority + then within category order
            const priorities = categoryPriorityForDocKind(docKind);
            const sortKey = (f: ArtifactFinding) => {
              const cat = String(f.category || 'other');
              const idx = priorities.indexOf(cat);
              return idx === -1 ? priorities.length : idx;
            };
            const sorted = [...findingsAll].sort((a, b) => sortKey(a) - sortKey(b));

            const visibleFindings = isExpanded
              ? sorted.slice(0, densityView.maxFindingsPerFile)
              : sorted.slice(
                  0,
                  Math.min(densityView.maxFindingsPerFile, density === 'compact' ? 3 : 4)
                );

            const missingCount = Array.isArray(file.missingCoverage)
              ? file.missingCoverage.length
              : 0;
            const insightsStatus = String(file.insightsStatus || '').toLowerCase();

            return (
              <div
                key={fileId}
                className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 overflow-hidden"
              >
                <div className="px-4 py-3 flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                  <div className="w-8 h-8 rounded-lg bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-200">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div
                      className="text-sm font-semibold text-slate-900 dark:text-white truncate"
                      dir="auto"
                    >
                      {file.fileName}
                    </div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-full bg-slate-200/70 dark:bg-slate-700/60">
                        {docKind}
                      </span>
                      {docKind === 'unknown' && (
                        <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                          ⚠️ type not provided
                        </span>
                      )}
                      {insightsStatus &&
                        !['ready', 'ready_with_warnings'].includes(insightsStatus) && (
                          <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                            understanding: {insightsStatus}
                          </span>
                        )}
                      {missingCount > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                          ⚠️ {missingCount} coverage gap{missingCount === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={() => toggleFile(fileId)}
                    className="text-xs font-medium text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 flex items-center gap-1"
                    title={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    {isExpanded ? (
                      <>
                        <ChevronDown className="w-4 h-4" /> Less
                      </>
                    ) : (
                      <>
                        <ChevronRight className="w-4 h-4" /> More
                      </>
                    )}
                  </button>
                </div>

                <div className="p-4">
                  <div className="space-y-3">
                    {briefText && (
                      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 px-3 py-2">
                        <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                          Brief (as provided)
                        </div>
                        <div className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap leading-relaxed">
                          {briefText}
                        </div>
                      </div>
                    )}

                    {sorted.length === 0 ? (
                      // ✅ ENTERPRISE UX: Never show “no findings” copy to users.
                      // If a brief exists, it is already displayed above. Otherwise, show a neutral prep state.
                      briefText ? null : (
                        <div className="text-sm text-slate-600 dark:text-slate-300">
                          Understanding is still being prepared for this file.
                        </div>
                      )
                    ) : (
                      <>
                        {visibleFindings.map((f: ArtifactFinding, idx: number) => {
                          const cat = String(f.category || 'other');
                          const icon = CATEGORY_ICON[cat]?.icon || '•';
                          const anchors = Array.isArray(f.citationAnchors) ? f.citationAnchors : [];
                          const quote = Array.isArray(f.quotes) ? String(f.quotes[0] || '') : '';

                          const chips = anchors.slice(0, density === 'analytical' ? 3 : 2);

                          const isCitable = isFindingCitable(f);

                          return (
                            <div key={`${cat}-${idx}`} className="flex items-start gap-2">
                              <div className="mt-0.5 w-5 flex-shrink-0 text-center text-sm">
                                {icon}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-start gap-2">
                                  <div className="text-sm text-slate-800 dark:text-slate-100 leading-relaxed flex-1">
                                    {f.text}
                                  </div>
                                  {!isCitable && (
                                    <span
                                      className="mt-0.5 px-2 py-0.5 rounded-full text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800/40"
                                      title="This finding is missing a quote and/or citation anchors."
                                    >
                                      uncited
                                    </span>
                                  )}
                                </div>

                                {/* Citations (compact chips) */}
                                {chips.length > 0 && (
                                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                                    {chips.map((a: ArtifactCitationAnchor, i: number) => {
                                      const labelParts = [
                                        a.page ? `p${a.page}` : null,
                                        a.sheetName ? `${a.sheetName}` : null,
                                        a.cellRange ? `${a.cellRange}` : null,
                                        a.lineNumber ? `L${a.lineNumber}` : null,
                                      ].filter(Boolean);

                                      const label =
                                        labelParts.length > 0 ? labelParts.join(' • ') : 'cite';
                                      const tooltipParts = [
                                        a.ledgerUnitId ? `Ledger unit: ${a.ledgerUnitId}` : null,
                                        typeof a.chunkIndex === 'number'
                                          ? `Chunk: C${a.chunkIndex}`
                                          : null,
                                      ].filter(Boolean);

                                      return (
                                        <button
                                          key={i}
                                          onClick={() => {
                                            if (!onCitationClick) return;
                                            onCitationClick(
                                              buildCitationObject({
                                                file,
                                                anchor: a,
                                                quote: quote || null,
                                              })
                                            );
                                          }}
                                          className="px-2 py-0.5 rounded-full text-[11px] bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:border-amber-300 dark:hover:border-amber-800 transition-colors"
                                          title={
                                            tooltipParts.length > 0
                                              ? tooltipParts.join(' • ')
                                              : 'Open source'
                                          }
                                        >
                                          {label}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}

                                {/* Quote (analytical only, collapsible density) */}
                                {densityView.showQuotes && quote && (
                                  <div className="mt-2 pl-3 border-l-2 border-slate-200 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-300">
                                    “{quote.slice(0, 420)}
                                    {quote.length > 420 ? '…' : ''}”
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}

                    {sorted.length > visibleFindings.length && density !== 'analytical' && (
                      <button
                        onClick={() => toggleFile(fileId)}
                        className="mt-1 text-xs text-amber-700 dark:text-amber-300 hover:underline"
                      >
                        Show {sorted.length - visibleFindings.length} more finding
                        {sorted.length - visibleFindings.length === 1 ? '' : 's'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// SECTION HEADER BLOCK - Looks like a CARD HEADER, not plain text
// ═══════════════════════════════════════════════════════════════════════════

const SectionHeader: React.FC<{ block: SectionHeaderBlock; emoji?: string }> = ({
  block,
  emoji,
}) => {
  const level = block.level ?? 2;

  // Level 1: Major section - Full card header style
  if (level === 1) {
    return (
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 p-[1px]">
        <div className="flex items-center gap-3 bg-white dark:bg-slate-900 rounded-xl px-4 py-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 text-white">
            {emoji ? <span className="text-lg">{emoji}</span> : <Hash className="w-4 h-4" />}
          </div>
          <h2 className="text-lg font-bold text-slate-800 dark:text-white tracking-tight">
            {block.text}
          </h2>
        </div>
      </div>
    );
  }

  // Level 2: Sub-section - Bordered card style
  if (level === 2) {
    return (
      <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg">
        <div className="flex items-center justify-center w-6 h-6 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
          {emoji ? <span className="text-sm">{emoji}</span> : <BookOpen className="w-3.5 h-3.5" />}
        </div>
        <h3 className="text-base font-semibold text-slate-700 dark:text-white">{block.text}</h3>
      </div>
    );
  }

  // Level 3: Minor section - Underlined style
  return (
    <div className="flex items-center gap-2 pb-2 border-b border-amber-200 dark:border-amber-800/50">
      {emoji && <span className="text-sm">{emoji}</span>}
      <h4 className="text-sm font-medium text-amber-700 dark:text-amber-400 uppercase tracking-wide">
        {block.text}
      </h4>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// SUBHEADER - Numbered section style
// ═══════════════════════════════════════════════════════════════════════════

const Subheader: React.FC<{ block: SubheaderBlock; emoji?: string }> = ({ block, emoji }) => (
  <div className="flex items-center gap-3 py-2">
    {block.label && (
      <span className="flex items-center justify-center min-w-[2rem] h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white text-sm font-bold shadow-sm">
        {block.label.replace('.', '')}
      </span>
    )}
    {emoji && !block.label && <span className="text-lg">{emoji}</span>}
    <span className="text-base font-semibold text-slate-800 dark:text-slate-100">{block.text}</span>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// PARAGRAPH BLOCK - Contained with visual indicator
// ═══════════════════════════════════════════════════════════════════════════

const Paragraph: React.FC<{ block: ParagraphBlock; emoji?: string }> = ({ block, emoji }) => (
  <div className="relative pl-4 border-l-2 border-slate-200 dark:border-slate-700">
    <div className="text-slate-700 dark:text-white leading-relaxed">
      {emoji && <span className="mr-2 text-lg">{emoji}</span>}
      <MarkdownText content={block.text} />
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// BULLET LIST BLOCK - Card-contained list with visual bullets
// ═══════════════════════════════════════════════════════════════════════════

const BulletList: React.FC<{ block: BulletListBlock; showEmojis?: boolean }> = ({
  block,
  showEmojis,
}) => (
  <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 overflow-hidden">
    {/* List header */}
    <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
      <List className="w-4 h-4 text-slate-500 dark:text-slate-400" />
      <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
        Key Points
      </span>
      <span className="ml-auto text-xs text-slate-400 dark:text-slate-500">
        {block.items.length} items
      </span>
    </div>
    {/* List items */}
    <ul className="divide-y divide-slate-200/50 dark:divide-slate-700/50">
      {block.items.map((item, idx) => (
        <li
          key={idx}
          className="flex items-start gap-3 px-4 py-3 hover:bg-slate-100/50 dark:hover:bg-slate-800/50 transition-colors"
        >
          <div className="flex-shrink-0 mt-1.5 w-2 h-2 rounded-full bg-gradient-to-br from-amber-400 to-orange-500" />
          <span className="flex-1 text-slate-700 dark:text-white text-sm leading-relaxed">
            <MarkdownText content={item} simple />
          </span>
        </li>
      ))}
    </ul>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// NUMBERED LIST BLOCK - Horizontal document chips style
// ═══════════════════════════════════════════════════════════════════════════

const NumberedList: React.FC<{ block: NumberedListBlock }> = ({ block }) => (
  <div className="space-y-2">
    {/* List header */}
    <div className="flex items-center gap-2 px-1">
      <ListOrdered className="w-4 h-4 text-amber-500 dark:text-amber-400" />
      <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
        Ordered Items
      </span>
    </div>
    {/* Numbered items as cards */}
    <div className="space-y-2">
      {block.items.map((item, idx) => (
        <div
          key={idx}
          className="flex items-start gap-4 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-sm transition-all"
        >
          <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 text-white font-bold text-sm shadow-sm">
            {idx + 1}
          </div>
          <div className="flex-1 pt-1 text-slate-700 dark:text-white text-sm leading-relaxed">
            <MarkdownText content={item} simple />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// TABLE BLOCK - Professional data table
// ═══════════════════════════════════════════════════════════════════════════

const Table: React.FC<{ block: TableBlock }> = ({ block }) => (
  <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
    {/* Table header */}
    <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
      <Table2 className="w-4 h-4 text-slate-500 dark:text-slate-400" />
      <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
        {block.caption || 'Data Table'}
      </span>
      <span className="ml-auto text-xs text-slate-400 dark:text-slate-500">
        {block.rows.length} rows × {block.columns.length} cols
      </span>
    </div>
    {/* Table content */}
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead className="bg-slate-50 dark:bg-slate-800/80">
          <tr>
            {block.columns.map((col, idx) => (
              <th
                key={idx}
                scope="col"
                className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-white uppercase tracking-wider border-b border-slate-200 dark:border-slate-700"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
          {block.rows.map((row, rowIdx) => (
            <tr
              key={rowIdx}
              className="hover:bg-amber-50/50 dark:hover:bg-amber-900/10 transition-colors"
            >
              {row.map((cell, cellIdx) => (
                <td key={cellIdx} className="px-4 py-3 text-sm text-slate-700 dark:text-white">
                  <MarkdownText content={cell} simple />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// CODE BLOCK - IDE-style code container
// ═══════════════════════════════════════════════════════════════════════════

const CodeBox: React.FC<{ block: CodeBlock }> = ({ block }) => {
  const { copied, copy } = useCopyToClipboard();

  const handleDownload = () => {
    const blob = new Blob([block.content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ext =
      block.language === 'javascript' ? 'js' : block.language === 'typescript' ? 'ts' : 'txt';
    a.href = url;
    a.download = `code.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-slate-900">
      {/* Code header */}
      <div className="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700">
        <div className="flex items-center gap-2">
          <Code2 className="w-4 h-4 text-slate-400" />
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">
            {block.language || 'code'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {block.allowCopy !== false && (
            <button
              onClick={() => copy(block.content)}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-slate-400 hover:text-white border border-slate-600 hover:border-slate-500 rounded-md transition-colors"
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          )}
          {block.allowDownload && (
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-slate-400 hover:text-white border border-slate-600 hover:border-slate-500 rounded-md transition-colors"
            >
              <Download className="w-3 h-3" />
              Download
            </button>
          )}
        </div>
      </div>
      {/* Code content */}
      <pre className="p-4 overflow-x-auto text-sm text-slate-300 font-mono leading-relaxed">
        <code>{block.content}</code>
      </pre>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// CALLOUT BLOCK - Prominent alert-style container
// ═══════════════════════════════════════════════════════════════════════════

const Callout: React.FC<{ block: CalloutBlock; emoji?: string }> = ({ block, emoji }) => {
  const variantConfig: Record<
    CalloutVariant,
    {
      bg: string;
      border: string;
      icon: React.ReactNode;
      iconBg: string;
      text: string;
    }
  > = {
    info: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      icon: <Info className="w-4 h-4" />,
      iconBg: 'bg-blue-100 dark:bg-blue-800/50 text-blue-600 dark:text-blue-400',
      text: 'text-blue-800 dark:text-blue-200',
    },
    success: {
      bg: 'bg-emerald-50 dark:bg-emerald-900/20',
      border: 'border-emerald-200 dark:border-emerald-800',
      icon: <CheckCircle2 className="w-4 h-4" />,
      iconBg: 'bg-emerald-100 dark:bg-emerald-800/50 text-emerald-600 dark:text-emerald-400',
      text: 'text-emerald-800 dark:text-emerald-200',
    },
    warning: {
      bg: 'bg-amber-50 dark:bg-amber-900/20',
      border: 'border-amber-200 dark:border-amber-800',
      icon: <AlertTriangle className="w-4 h-4" />,
      iconBg: 'bg-amber-100 dark:bg-amber-800/50 text-amber-600 dark:text-amber-400',
      text: 'text-amber-800 dark:text-amber-200',
    },
    error: {
      bg: 'bg-rose-50 dark:bg-rose-900/20',
      border: 'border-rose-200 dark:border-rose-800',
      icon: <AlertCircle className="w-4 h-4" />,
      iconBg: 'bg-rose-100 dark:bg-rose-800/50 text-rose-600 dark:text-rose-400',
      text: 'text-rose-800 dark:text-rose-200',
    },
  };

  const config = variantConfig[block.variant];

  return (
    <div className={`rounded-xl border-2 ${config.border} ${config.bg} overflow-hidden`}>
      <div className="flex items-start gap-3 p-4">
        <div
          className={`flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg ${config.iconBg}`}
        >
          {emoji ? <span className="text-lg">{emoji}</span> : config.icon}
        </div>
        <div className="flex-1 min-w-0">
          {block.title && <h4 className={`font-semibold ${config.text} mb-1`}>{block.title}</h4>}
          <p className={`text-sm ${config.text} leading-relaxed`}>
            <MarkdownText content={block.text} simple />
          </p>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// STATUS LINE - Inline status indicator
// ═══════════════════════════════════════════════════════════════════════════

const StatusLine: React.FC<{ block: StatusLineBlock; emoji?: string }> = ({ block, emoji }) => {
  const toneConfig: Record<StatusTone, { bg: string; border: string; dot: string; text: string }> =
    {
      default: {
        bg: 'bg-slate-50 dark:bg-slate-800/50',
        border: 'border-slate-200 dark:border-slate-700',
        dot: 'bg-slate-400',
        text: 'text-slate-600 dark:text-slate-400',
      },
      success: {
        bg: 'bg-emerald-50 dark:bg-emerald-900/20',
        border: 'border-emerald-200 dark:border-emerald-800',
        dot: 'bg-emerald-500',
        text: 'text-emerald-700 dark:text-emerald-400',
      },
      warning: {
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        border: 'border-amber-200 dark:border-amber-800',
        dot: 'bg-amber-500',
        text: 'text-amber-700 dark:text-amber-400',
      },
      error: {
        bg: 'bg-rose-50 dark:bg-rose-900/20',
        border: 'border-rose-200 dark:border-rose-800',
        dot: 'bg-rose-500',
        text: 'text-rose-700 dark:text-rose-400',
      },
    };

  const config = toneConfig[block.tone ?? 'default'];

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${config.border} ${config.bg}`}
    >
      <div className={`w-2 h-2 rounded-full ${config.dot} animate-pulse`} />
      {emoji && <span className="text-sm">{emoji}</span>}
      <span className={`text-xs font-medium ${config.text}`}>{block.text}</span>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PROGRESS LINE - Visual progress indicator
// ═══════════════════════════════════════════════════════════════════════════

const ProgressLine: React.FC<{ block: ProgressLineBlock; emoji?: string }> = ({ block, emoji }) => {
  const percent = Math.max(0, Math.min(100, block.progressPercent ?? 0));

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30 p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {emoji && <span className="text-lg">{emoji}</span>}
          <span className="text-sm font-medium text-slate-700 dark:text-white">
            {block.message}
          </span>
        </div>
        {block.step && block.totalSteps && (
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Step {block.step}/{block.totalSteps}
          </span>
        )}
      </div>
      {block.progressPercent !== undefined && (
        <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-500"
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// METRICS LIST - Dashboard-style metrics
// ═══════════════════════════════════════════════════════════════════════════

const MetricsList: React.FC<{ block: MetricsListBlock; showEmojis: boolean }> = ({
  block,
  showEmojis,
}) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
    {block.items.map((item, idx) => (
      <div
        key={idx}
        className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50"
      >
        {showEmojis && item.emoji && <span className="text-xl">{item.emoji}</span>}
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-700 dark:text-white truncate">
            {item.label}
          </div>
          {item.value && (
            <div className="text-xs text-slate-500 dark:text-slate-400">{item.value}</div>
          )}
        </div>
      </div>
    ))}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// DIVIDER - Visual section separator
// ═══════════════════════════════════════════════════════════════════════════

const Divider: React.FC = () => (
  <div className="relative py-1.5 mt-1 -mb-1">
    <div className="absolute inset-0 flex items-center">
      <div className="w-full border-t border-slate-200 dark:border-slate-700" />
    </div>
    <div className="relative flex justify-center">
      <span className="bg-slate-100 dark:bg-neutral-800 px-3 rounded-full">
        <Sparkles className="w-3.5 h-3.5 text-amber-400" />
      </span>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// CITATION BLOCK - Now handled by MessageFooterTabs, return null to avoid duplication
// ═══════════════════════════════════════════════════════════════════════════

const Citation: React.FC<{
  block: CitationBlock;
  onCitationClick?: (citation: any) => void;
}> = () => {
  // Sources are now displayed in the unified MessageFooterTabs component
  // Returning null to prevent duplicate "SOURCES" box in the message content
  return null;
};

// ═══════════════════════════════════════════════════════════════════════════
// EXPANDABLE BLOCK - Collapsible content section
// ═══════════════════════════════════════════════════════════════════════════

interface ExpandableProps {
  block: ExpandableBlock;
  showEmojis: boolean;
  compactMode: boolean;
  onButtonClick?: (action: string) => void;
  onCitationClick?: (citation: any) => void;
}

const Expandable: React.FC<ExpandableProps> = ({
  block,
  showEmojis,
  compactMode,
  onButtonClick,
  onCitationClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(block.defaultExpanded ?? false);

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
      >
        <div className="flex-shrink-0 text-slate-500 dark:text-slate-400">
          {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </div>
        <span className="flex-1 text-left text-sm font-medium text-slate-700 dark:text-white">
          {block.summary}
        </span>
      </button>
      {isExpanded && block.content && (
        <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50">
          <RichChatRenderer
            blocks={block.content}
            showEmojis={showEmojis}
            compactMode={compactMode}
            onButtonClick={onButtonClick}
            onCitationClick={onCitationClick}
          />
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// BUTTON GROUP - Action buttons
// ═══════════════════════════════════════════════════════════════════════════

const ButtonGroup: React.FC<{
  block: ButtonGroupBlock;
  onButtonClick?: (action: string) => void;
}> = ({ block, onButtonClick }) => (
  <div className="flex flex-wrap gap-2">
    {block.buttons.map((button, idx) => (
      <button
        key={idx}
        onClick={() => onButtonClick?.(button.action)}
        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
          button.variant === 'primary'
            ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 shadow-sm'
            : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
        }`}
      >
        {button.label}
      </button>
    ))}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT
// ═══════════════════════════════════════════════════════════════════════════

export default RichChatRenderer;
